/**
 * El recibo como PDF, del tamaño exacto del rollo.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ POR QUÉ UN PDF Y NO UNA PÁGINA HTML
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Lo pidió el dueño con una impresora recién comprada delante: «tiene que
 * funcionar como Fina — en Fina se puede imprimir sin instalar drivers ni
 * nada».
 *
 * Softfi ya imprimía sin drivers, con `imprimirHoja.ts`, pero tenía un agujero
 * que se veía en cuanto lo probabas de verdad: el tamaño del papel se pide con
 * `@page { size: 80mm 91mm }`, y **el navegador descarta esa regla en silencio
 * si el destino elegido no admite ese tamaño**. Con «Fax» o «Guardar como PDF»
 * en el desplegable de Destino, el recibo sale diminuto en medio de una hoja
 * carta, con los encabezados del navegador encima. Exactamente lo que pasó.
 *
 * Un PDF lleva su geometría DENTRO, en el `MediaBox`. No es una sugerencia al
 * navegador: es el documento diciendo cuánto mide. Se abre, se imprime con el
 * driver que Windows instaló solo, y sale del tamaño del rollo.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ ESCRITO A MANO, Y NO POR CABEZONERÍA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Una librería de PDF son entre 300 KB y 1 MB de JavaScript. Softfi se abre en
 * teléfonos de gama baja con datos móviles, y el POS tiene que arrancar rápido
 * y funcionar sin internet: un megabyte más para imprimir un papel de veinte
 * líneas no sale a cuenta.
 *
 * Y no hace falta. Un recibo es texto monoespaciado, y eso en PDF es casi
 * trivial:
 *
 *   · **Courier** es una de las catorce fuentes que TODO visor de PDF trae
 *     obligatoriamente. No hay que incrustar nada, y es de ancho fijo — que es
 *     justo lo que necesita un ticket para alinear los precios a la derecha.
 *   · El contenido es una lista de «pon este texto en esta posición».
 *   · La estructura son cinco objetos y una tabla de posiciones.
 *
 * Doscientas líneas y cero dependencias.
 */

import type { AnchoPapel } from "./escpos";
import type { LineaDeTicket } from "./ticketLineas";

// =============================================================================
//  MEDIDAS
// =============================================================================

/**
 * Un punto PDF es 1/72 de pulgada. Todo en este archivo va en puntos.
 *
 * 80 mm = 226,77 pt · 58 mm = 164,41 pt
 */
const PUNTOS_POR_MM = 72 / 25.4;

/**
 * ⚠️ EL ANCHO **IMPRIMIBLE**, QUE NO ES EL DEL PAPEL.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  AQUÍ ESTABA EL RECIBO CORTADO POR LA DERECHA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Esto decía 58 y 80 —el ancho del rollo— y era el error. Una térmica NO
 * imprime hasta el borde del papel: el cabezal es más estrecho.
 *
 *   · Rollo de 58 mm → imprime 48 mm (384 puntos a 203 ppp)
 *   · Rollo de 80 mm → imprime 72 mm (576 puntos a 203 ppp)
 *
 * Lo dice el propio driver en su lista de tamaños: **«58(48)mm x 210 mm»**. El
 * número entre paréntesis es lo que de verdad cabe.
 *
 * Con la página a 58 mm, Windows la mandaba a un área de 48 y **recortaba el
 * 17 % de la derecha** — justo la columna de los importes, que es la que
 * cuenta. Se veía en la vista previa: «TOTAL 2,» y ahí se acababa.
 *
 * Y no escalaba para que cupiera: recortaba. Por eso la página tiene que medir
 * exactamente lo imprimible, y así se mapea uno a uno.
 */
const ANCHO_MM: Record<AnchoPapel, number> = { "58": 48, "80": 72 };

/**
 * El cuerpo de letra, por rollo.
 *
 * ⚠️ NO SE ELIGE AL GUSTO: se calcula para que quepan exactamente las columnas
 * del ticket. En Courier cada carácter mide 0,6 del cuerpo, así que:
 *
 *     cuerpo = (ancho útil del papel) / (columnas × 0,6)
 *
 * Con 48 columnas en 80 mm y 3 mm de margen a cada lado salen ~8,3 pt. Si se
 * pusiera más grande, la última columna —donde va el importe— se saldría del
 * papel y el cliente vería el total cortado.
 */
const ANCHO_DEL_CARACTER = 0.6;

/**
 * Margen a cada lado, en milímetros.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ ES HOLGURA DE SEGURIDAD, NO ESTÉTICA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Estuvo en 1 mm, apurando los 48 mm que el driver declara imprimibles. Sobre
 * el papel se seguía cortando un carácter o dos por la derecha — poco, pero
 * justo donde va el importe.
 *
 * En la vista previa cabía. En el papel no. Y esa diferencia es la clave: los
 * 48 mm del driver son **nominales**. La impresora de verdad imprime un pelo
 * menos, o empieza un pelo más a la derecha, y cada fabricante barato lo hace
 * un poco distinto.
 *
 * Contra eso no hay número exacto que valga: hay que dejar sitio.
 *
 * ⚠️ CON 2,5 mm TODAVÍA SE COMÍA MEDIA LETRA. Se probó en papel: el «USD» del
 * final salía «USI», con la última letra partida por el borde. Así que sube a 4.
 *
 * Y subirlo no es solo por esta impresora — es lo que hace que la siguiente no
 * dé el mismo problema. Estas térmicas se venden con cincuenta marcas y ninguna
 * publica su margen real; el único ajuste que vale para todas es el que sobra
 * un poco en la que tenemos delante.
 *
 * Con 4 mm por lado, en un rollo de 58 el contenido usa 40 de los 48 mm
 * imprimibles. Es justo lo que usan los tickets de toda la vida: 32 caracteres
 * a 1,25 mm cada uno.
 *
 * ⚠️ Cuesta un poco de tamaño de letra y vale la pena: un ticket con la letra
 * algo más chica se lee igual; uno con el total cortado no sirve para nada.
 */
const MARGEN_MM = 4;

/** Separación entre líneas, como múltiplo del cuerpo. */
const INTERLINEADO = 1.32;

/** Papel de sobra al final, en milímetros, para que la cuchilla no coma texto. */
const COLA_MM = 6;

// =============================================================================
//  ⚠️ EL TEXTO, EN BYTES QUE EL PDF ENTIENDA
// =============================================================================

/**
 * Los 32 caracteres que separan a WinAnsi (lo que usa el PDF) de Latin-1.
 *
 * ⚠️ Sin esto, un recibo con el símbolo del euro o unas comillas tipográficas
 * saldría con basura en medio. Son pocos pero aparecen: el guion largo lo mete
 * cualquiera al copiar un nombre desde WhatsApp.
 */
const WINANSI_ALTO: Record<string, number> = {
  "€": 0x80, "‚": 0x82, "ƒ": 0x83, "„": 0x84,
  "…": 0x85, "†": 0x86, "‡": 0x87, "ˆ": 0x88,
  "‰": 0x89, "Š": 0x8a, "‹": 0x8b, "Œ": 0x8c,
  "Ž": 0x8e, "‘": 0x91, "’": 0x92, "“": 0x93,
  "”": 0x94, "•": 0x95, "–": 0x96, "—": 0x97,
  "˜": 0x98, "™": 0x99, "š": 0x9a, "›": 0x9b,
  "œ": 0x9c, "ž": 0x9e, "Ÿ": 0x9f,
};

/**
 * Pasa un texto a los bytes que espera un PDF con codificación WinAnsi.
 *
 * ⚠️ WinAnsi y no UTF-8 a propósito. Con las catorce fuentes de siempre no se
 * puede usar Unicode sin incrustar la fuente entera, y ahí volvemos al
 * megabyte. WinAnsi cubre el español completo —áéíóú, ñ, ¡, ¿, ü— que es lo
 * que se imprime en un mostrador de Caracas o de Medellín.
 *
 * Lo que no cabe se sustituye por `?` en vez de romper el archivo: un recibo
 * con un carácter raro sigue siendo un recibo; uno que no abre, no.
 */
export function aWinAnsi(texto: string): number[] {
  const salida: number[] = [];
  for (const caracter of texto) {
    const punto = caracter.codePointAt(0)!;
    if (punto <= 0xff && !(punto >= 0x80 && punto <= 0x9f)) {
      salida.push(punto);
    } else if (WINANSI_ALTO[caracter] !== undefined) {
      salida.push(WINANSI_ALTO[caracter]);
    } else {
      salida.push(0x3f); // '?'
    }
  }
  return salida;
}

/**
 * Escapa un texto para meterlo entre paréntesis en el PDF.
 *
 * ⚠️ Los paréntesis y la barra invertida son la sintaxis del propio formato. Un
 * cliente llamado «Ana (la del kiosco)» rompería el archivo entero sin esto: el
 * visor cerraría la cadena antes de tiempo y el resto del recibo se leería como
 * instrucciones.
 */
export function escapar(bytes: number[]): string {
  let salida = "";
  for (const b of bytes) {
    if (b === 0x28 || b === 0x29 || b === 0x5c) salida += "\\";
    salida += String.fromCharCode(b);
  }
  return salida;
}

// =============================================================================
//  EL DOCUMENTO
// =============================================================================

/**
 * Convierte las líneas del recibo en un PDF de una página.
 *
 * Devuelve un `Blob` listo para abrir, guardar o mandar por WhatsApp.
 */
export function reciboComoPdf(
  lineas: LineaDeTicket[],
  ancho: AnchoPapel,
  columnasDelPapel: number,
): Blob {
  const anchoPagina = ANCHO_MM[ancho] * PUNTOS_POR_MM;
  const margen = MARGEN_MM * PUNTOS_POR_MM;
  const util = anchoPagina - margen * 2;

  // El cuerpo sale de cuántas columnas tienen que caber, no al revés.
  const cuerpo = util / (columnasDelPapel * ANCHO_DEL_CARACTER);
  const alto = cuerpo * INTERLINEADO;

  // ⚠️ Las líneas a doble tamaño ocupan el doble de alto. Contarlas como
  // normales dejaría el nombre del negocio y el TOTAL pisando la línea de
  // abajo, que son justo las dos que más se miran.
  const altoTotal =
    lineas.reduce((suma, l) => suma + (l.doble ? alto * 2 : alto), 0) +
    margen * 2 +
    COLA_MM * PUNTOS_POR_MM;

  // ── El contenido de la página ────────────────────────────────────────────
  const ordenes: string[] = ["BT"];
  let y = altoTotal - margen;
  let fuenteActual = "";

  for (const l of lineas) {
    const escala = l.doble ? 2 : 1;
    const tam = cuerpo * escala;
    y -= alto * escala;

    if (l.texto.trim() !== "") {
      /*
        ⚠️ La negrita es OTRA FUENTE, no un grosor.

        En PDF no existe «poner esto en negrita»: hay Courier y hay
        Courier-Bold, y son dos recursos distintos. Pedir grosor no haría nada
        y el TOTAL saldría igual que el resto.
      */
      const fuente = l.negrita || l.doble ? "F2" : "F1";
      if (fuente !== fuenteActual || escala !== 1) {
        ordenes.push(`/${fuente} ${tam.toFixed(2)} Tf`);
        fuenteActual = fuente;
      }

      const anchoTexto = l.texto.length * tam * ANCHO_DEL_CARACTER;
      const x =
        l.alineacion === "centro"
          ? margen + (util - anchoTexto) / 2
          : l.alineacion === "derecha"
            ? margen + util - anchoTexto
            : margen;

      ordenes.push(`1 0 0 1 ${Math.max(margen, x).toFixed(2)} ${y.toFixed(2)} Tm`);
      ordenes.push(`(${escapar(aWinAnsi(l.texto))}) Tj`);
      // Tras una línea a doble tamaño hay que volver al cuerpo normal.
      if (escala !== 1) fuenteActual = "";
    }
  }
  ordenes.push("ET");
  const contenido = ordenes.join("\n");

  // ── Los objetos del archivo ──────────────────────────────────────────────
  //
  // Un PDF mínimo son cinco: el catálogo, el árbol de páginas, la página, el
  // contenido y las fuentes. Nada más hace falta para texto.
  const objetos = [
    "<< /Type /Catalog /Pages 2 0 R >>",
    "<< /Type /Pages /Kids [3 0 R] /Count 1 >>",
    `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${anchoPagina.toFixed(2)} ${altoTotal.toFixed(2)}] ` +
      "/Resources << /Font << /F1 5 0 R /F2 6 0 R >> >> /Contents 4 0 R >>",
    `<< /Length ${contenido.length} >>\nstream\n${contenido}\nendstream`,
    "<< /Type /Font /Subtype /Type1 /BaseFont /Courier /Encoding /WinAnsiEncoding >>",
    "<< /Type /Font /Subtype /Type1 /BaseFont /Courier-Bold /Encoding /WinAnsiEncoding >>",
  ];

  /*
    ⚠️ LA TABLA DE POSICIONES TIENE QUE CUADRAR AL BYTE.

    El `xref` dice en qué byte empieza cada objeto, y el visor va directo ahí.
    Si sobra o falta uno, el archivo no abre — y no avisa de por qué.

    Por eso se va midiendo con la longitud real de lo que ya se escribió, en vez
    de calcularlo aparte: dos cuentas separadas acaban discrepando.
  */
  let pdf = "%PDF-1.4\n";
  const posiciones: number[] = [];

  objetos.forEach((cuerpoObjeto, i) => {
    posiciones.push(pdf.length);
    pdf += `${i + 1} 0 obj\n${cuerpoObjeto}\nendobj\n`;
  });

  const inicioTabla = pdf.length;
  pdf += `xref\n0 ${objetos.length + 1}\n0000000000 65535 f \n`;
  for (const p of posiciones) {
    pdf += `${String(p).padStart(10, "0")} 00000 n \n`;
  }
  pdf +=
    `trailer\n<< /Size ${objetos.length + 1} /Root 1 0 R >>\n` +
    `startxref\n${inicioTabla}\n%%EOF`;

  /*
    ⚠️ SE ESCRIBE BYTE A BYTE, NO COMO TEXTO.

    `new Blob([pdf])` codificaría en UTF-8, y ahí una «ñ» pasaría a ocupar DOS
    bytes. Todas las posiciones de la tabla se correrían y el PDF no abriría —
    pero solo en los recibos con acentos, que es el peor fallo posible: el que
    no sale al probarlo con «Corte de cabello» y sí con «Jabón».
  */
  const bytes = new Uint8Array(pdf.length);
  for (let i = 0; i < pdf.length; i++) bytes[i] = pdf.charCodeAt(i) & 0xff;

  return new Blob([bytes], { type: "application/pdf" });
}
